import React, { useState, useRef, useEffect, useMemo } from "react";

const normalizeId = (id) => {
    if (id == null || id === "") return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : id;
};

export default function SearchableApartmentSelect({
    apartments = [],
    value,
    onChange,
    placeholder = "Select Apartment",
    loading = false,
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const containerRef = useRef(null);

    const selected = useMemo(() => {
        const normalizedValue = normalizeId(value);
        return apartments.find((a) => normalizeId(a.id) === normalizedValue) || null;
    }, [apartments, value]);

    const filtered = useMemo(() => {
        const term = search.trim().toLowerCase();
        const sorted = [...apartments].sort((a, b) =>
            String(a.number || "").localeCompare(String(b.number || ""), undefined, {
                numeric: true,
                sensitivity: "base",
            })
        );
        if (!term) return sorted;
        return sorted.filter((a) =>
            String(a.number || "").toLowerCase().includes(term)
        );
    }, [apartments, search]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (apt) => {
        onChange(normalizeId(apt.id));
        setOpen(false);
        setSearch("");
    };

    return (
        <div className={`searchable-select ${open ? "is-open" : ""}`} ref={containerRef}>
            <button
                type="button"
                className={`searchable-select-trigger ${open ? "open" : ""}`}
                onClick={() => setOpen((prev) => !prev)}
                aria-haspopup="listbox"
                aria-expanded={open}
                disabled={loading}
            >
                <span>
                    {loading
                        ? "Loading apartments..."
                        : selected
                            ? `Apartment ${selected.number}`
                            : placeholder}
                </span>
                <span className="searchable-select-chevron">{open ? "▲" : "▼"}</span>
            </button>

            {open && (
                <div className="searchable-select-panel glass">
                    <input
                        type="text"
                        className="searchable-select-search"
                        placeholder="Search apartment number..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        autoFocus
                    />
                    <ul className="searchable-select-list" role="listbox">
                        {loading ? (
                            <li className="searchable-select-empty">Loading apartments...</li>
                        ) : filtered.length === 0 ? (
                            <li className="searchable-select-empty">
                                {apartments.length === 0
                                    ? "No apartments available"
                                    : "No apartments found"}
                            </li>
                        ) : (
                            filtered.map((apt) => (
                                <li key={apt.id}>
                                    <button
                                        type="button"
                                        className={`searchable-select-option ${
                                            normalizeId(apt.id) === normalizeId(value) ? "selected" : ""
                                        }`}
                                        onClick={() => handleSelect(apt)}
                                        role="option"
                                        aria-selected={normalizeId(apt.id) === normalizeId(value)}
                                    >
                                        Apartment {apt.number}
                                    </button>
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
